import { InputHTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, label, description, size = 'md', disabled, checked, id, ...props }, ref) => {
    const inputId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    const sizes = {
      sm: {
        track: 'w-8 h-4',
        thumb: 'w-3 h-3',
        translate: 'translate-x-4',
        offset: 'translate-x-0.5',
      },
      md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5',
        translate: 'translate-x-5',
        offset: 'translate-x-0.5',
      },
      lg: {
        track: 'w-14 h-7',
        thumb: 'w-6 h-6',
        translate: 'translate-x-7',
        offset: 'translate-x-0.5',
      },
    };

    const sizeConfig = sizes[size];

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-center gap-3 cursor-pointer',
          disabled && 'cursor-not-allowed opacity-50',
          className
        )}
      >
        <div className="relative inline-flex items-center">
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            className="sr-only peer"
            disabled={disabled}
            checked={checked}
            {...props}
          />
          <div
            className={cn(
              'rounded-full transition-colors duration-200',
              'peer-focus:ring-2 peer-focus:ring-[color:rgba(var(--ids-brand-rgb,59,130,246),0.40)] peer-focus:ring-offset-0',
              'bg-[color:var(--ids-border-strong,rgba(255,255,255,0.14))] peer-checked:bg-[color:var(--ids-brand,#3B82F6)]',
              sizeConfig.track
            )}
          />
          <div
            className={cn(
              'absolute rounded-full bg-white shadow-sm transition-transform duration-200',
              'top-1/2 -translate-y-1/2',
              checked ? sizeConfig.translate : sizeConfig.offset,
              sizeConfig.thumb
            )}
          />
        </div>
        {(label || description) && (
          <div className="flex flex-col">
            {label && (
              <span className="text-sm font-medium text-[color:var(--ids-text,#F8FAFC)]">
                {label}
              </span>
            )}
            {description && (
              <span className="text-xs text-[color:var(--ids-text-subtle,#7E8DA6)]">
                {description}
              </span>
            )}
          </div>
        )}
      </label>
    );
  }
);

Switch.displayName = 'Switch';
